import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {alertModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";

const client = new MdbsCore.DataMigrationClient();
const auditLogClient = new MdbsCore.AuditLogClient();

// Identifier the core data-migration manager writes its startup runs under.
const AUDIT_CATEGORY = "DataMigration";

@Module({namespaced: true, name: 'dataMigration', store: store})
export default class DataMigrationModule extends BaseModule {
    private _statuses: MdbsCore.DataMigrationStatusDto[] = [];
    private _report: MdbsCore.DataMigrationReportDto | null = null;
    private _history: MdbsCore.AuditLogEntryDto[] = [];

    // Per-migration audit log: our own migrations write their entries under their migration id as
    // identifier, so they are fetched separately and on demand instead of with the startup runs.
    private _migrationHistory: MdbsCore.AuditLogEntryDto[] = [];
    private _migrationHistoryId: string | null = null;

    get statuses(): MdbsCore.DataMigrationStatusDto[] {
        return this._statuses;
    }

    get report(): MdbsCore.DataMigrationReportDto | null {
        return this._report;
    }

    get history(): MdbsCore.AuditLogEntryDto[] {
        return this._history;
    }

    get migrationHistory(): MdbsCore.AuditLogEntryDto[] {
        return this._migrationHistory;
    }

    /** Migration id the loaded per-migration history belongs to, or null if nothing was requested yet. */
    get migrationHistoryId(): string | null {
        return this._migrationHistoryId;
    }

    @Mutation
    setStatuses(statuses: MdbsCore.DataMigrationStatusDto[]) {
        this._statuses = statuses;
    }

    @Mutation
    setReport(report: MdbsCore.DataMigrationReportDto | null) {
        this._report = report;
    }

    @Mutation
    setHistory(entries: MdbsCore.AuditLogEntryDto[]) {
        this._history = entries;
    }

    @Mutation
    setMigrationHistory(payload: { migrationId: string | null, entries: MdbsCore.AuditLogEntryDto[] }) {
        this._migrationHistoryId = payload.migrationId;
        this._migrationHistory = payload.entries;
    }

    /** Clears everything; audit entries must not survive a logout into the next session. */
    @Mutation
    reset() {
        this._statuses = [];
        this._report = null;
        this._history = [];
        this._migrationHistory = [];
        this._migrationHistoryId = null;
    }

    @Action({rawError: true})
    async loadAll() {
        this.startLoading();
        try {
            const statuses = await client.getAll();
            this.setStatuses(statuses ?? []);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async runAll() {
        this.startLoading();
        this.setReport(null);
        try {
            const report = await client.runAll();
            this.setReport(report);
            await this.loadAll();
            return report;
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async loadHistory() {
        this.startLoading();
        try {
            const result = await auditLogClient.getAuditLogEntries(
                0, 200, [AUDIT_CATEGORY], undefined, undefined, undefined, undefined);
            this.setHistory(result?.items ?? []);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async exportHistory() {
        try {
            return await auditLogClient.exportAuditLog([AUDIT_CATEGORY], undefined, undefined, undefined, undefined);
        } catch (e) {
            await alertModule.handleException(e);
        }
    }

    /** Loads the audit log of a single migration, which writes under its own id as identifier. */
    @Action({rawError: true})
    async loadMigrationHistory(migrationId: string) {
        if (!migrationId) {
            this.setMigrationHistory({migrationId: null, entries: []});
            return;
        }

        this.startLoading();
        try {
            const result = await auditLogClient.getAuditLogEntries(
                0, 200, [migrationId], undefined, undefined, undefined, undefined);
            this.setMigrationHistory({migrationId, entries: result?.items ?? []});
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async exportMigrationHistory(migrationId: string) {
        if (!migrationId) return;
        try {
            return await auditLogClient.exportAuditLog([migrationId], undefined, undefined, undefined, undefined);
        } catch (e) {
            await alertModule.handleException(e);
        }
    }

    @Action({rawError: true})
    async resetMigrationHistory() {
        this.setMigrationHistory({migrationId: null, entries: []});
    }

    @Action({rawError: true})
    async run(payload: { migrationId: string, force: boolean }) {
        this.startLoading();
        this.setReport(null);
        try {
            const report = await client.run(payload.migrationId, payload.force);
            this.setReport(report);
            await this.loadAll();
            return report;
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }
}
